/**
 * Profile Page
 * 
 * User profile management with persona viewing/editing.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    User, Shield, Heart, Settings, ChevronRight,
    Lock, Unlock, Trophy, Star, Edit2, Save, X,
    Users, Link2, AlertTriangle, Loader2, Zap, Crown
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import Spinner from '../components/Spinner';
import ProfileCompletionRing from '../components/ProfileCompletionRing';
import PersonalDetailsForm from '../components/PersonalDetailsForm';
import SafetyPanel from '../components/SafetyPanel';
import HardLimitsPanel from '../components/HardLimitsPanel';
import MyRatings from '../components/MyRatings';
import ForYouWidget from '../components/ForYouWidget';
import QuickRateMode from '../components/QuickRateMode';
import LustWitchWidget from '../components/LustWitchWidget';
import PersonaCreationModal from '../components/PersonaCreationModal';
import { API_BASE } from '../utils/api';

// Completion tier badges
const TIER_CONFIG = {
    incomplete: { label: 'New', color: 'text-gray-400', bg: 'bg-gray-800' },
    basic: { label: 'Basic', color: 'text-blue-400', bg: 'bg-blue-900/30' },
    standard: { label: 'Standard', color: 'text-green-400', bg: 'bg-green-900/30' },
    complete: { label: 'Complete', color: 'text-purple-400', bg: 'bg-purple-900/30' },
    detailed: { label: 'Detailed', color: 'text-amber-400', bg: 'bg-amber-900/30' }
};

function ProfilePage() {
    const { personaId } = useParams();
    const { user, personas, activePersona, setActivePersona, fetchPersonas, isAuthenticated, getAuthHeaders } = useAuth();

    const [persona, setPersona] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOwner, setIsOwner] = useState(false);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [settingPrimary, setSettingPrimary] = useState(false);

    // Quick rate mode state
    const [showQuickRate, setShowQuickRate] = useState(false);
    const [unratedItems, setUnratedItems] = useState([]);

    // Persona management modal
    const [showPersonaModal, setShowPersonaModal] = useState(false);
    const [creatingNewPersona, setCreatingNewPersona] = useState(false);

    // Settings modal
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    // Edit form state
    const [editForm, setEditForm] = useState({
        display_name: '',
        bio: '',
        visibility: 'private'
    });

    // Fetch persona data
    useEffect(() => {
        const fetchPersona = async () => {
            setLoading(true);
            setError(null);

            try {
                const headers = await getAuthHeaders();

                const targetId = personaId || activePersona?.id;
                if (!targetId) {
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${API_BASE}/api/profiles/persona/${targetId}`, { headers });

                if (!response.ok) {
                    if (response.status === 403) {
                        setError('This profile is private');
                    } else if (response.status === 404) {
                        setError('Profile not found');
                    } else {
                        throw new Error('Failed to fetch profile');
                    }
                    return;
                }

                const data = await response.json();
                setPersona(data.persona);
                setIsOwner(data.isOwner);

                // Initialize edit form
                setEditForm({
                    display_name: data.persona.display_name || '',
                    bio: data.persona.bio || '',
                    visibility: data.persona.visibility || 'private'
                });
            } catch (err) {
                console.error('Error fetching persona:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchPersona();
    }, [personaId, activePersona, getAuthHeaders]);

    const handleSave = async () => {
        if (!persona) return;

        setSaving(true);
        try {
            const authHeaders = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/api/profiles/persona/${persona.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify(editForm)
            });

            if (!response.ok) throw new Error('Failed to save');

            setPersona(prev => ({ ...prev, ...editForm }));
            setEditing(false);
            fetchPersonas(); // Refresh personas list
        } catch (err) {
            console.error('Save error:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleSetPrimary = async () => {
        if (!persona || persona.is_primary) return;

        setSettingPrimary(true);
        try {
            const authHeaders = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/api/profiles/persona/${persona.id}/set-primary`, {
                method: 'PUT',
                headers: authHeaders
            });

            if (!response.ok) throw new Error('Failed to set primary');

            setPersona(prev => ({ ...prev, is_primary: true }));
            fetchPersonas(); // Refresh personas list
        } catch (err) {
            console.error('Set primary error:', err);
        } finally {
            setSettingPrimary(false);
        }
    };

    // Not authenticated - show sign in prompt
    if (!isAuthenticated && !personaId) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-charcoal">
                <div className="glass-card p-12 text-center max-w-md">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-wine-900/30 flex items-center justify-center">
                        <User className="w-10 h-10 text-wine-400" />
                    </div>
                    <h2 className="text-2xl font-display font-bold text-cream mb-3">
                        Create Your Profile
                    </h2>
                    <p className="text-cream/60 mb-8">
                        Sign in to create personas, rate kinks, track your preferences,
                        and connect with compatible partners.
                    </p>
                    <div className="flex flex-col gap-3">
                        <Link
                            to="/login"
                            className="btn-primary w-full flex items-center justify-center gap-2"
                        >
                            Sign In
                        </Link>
                        <Link
                            to="/register"
                            className="btn-ghost w-full flex items-center justify-center gap-2"
                        >
                            Create Account
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state
    if (loading) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-charcoal">
                <Spinner size="lg" />
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-charcoal">
                <div className="glass-card p-12 text-center max-w-md">
                    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-cream mb-2">{error}</h2>
                    <Link to="/" className="btn-ghost mt-4">
                        Go Home
                    </Link>
                </div>
            </div>
        );
    }

    // No persona loaded - show creation form for authenticated users
    if (!persona && isAuthenticated) {
        const handleCreate = async () => {
            if (!editForm.display_name.trim()) return;
            setSaving(true);
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${API_BASE}/api/profiles/persona`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...headers
                    },
                    body: JSON.stringify({
                        display_name: editForm.display_name.trim(),
                        visibility: 'private'
                    })
                });

                if (!response.ok) {
                    const err = await response.json();
                    throw new Error(err.error || 'Failed to create persona');
                }

                // Refresh personas and reload page
                await fetchPersonas();
                window.location.reload();
            } catch (err) {
                console.error('Create error:', err);
                setError(err.message);
            } finally {
                setSaving(false);
            }
        };

        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-charcoal">
                <div className="glass-card p-12 text-center max-w-md">
                    <div className="w-20 h-20 mx-auto mb-6 bg-wine-900/30 flex items-center justify-center border border-wine-700/50">
                        <User className="w-10 h-10 text-wine-400" />
                    </div>
                    <h2 className="text-2xl font-display font-bold text-cream mb-3">
                        Create a Persona
                    </h2>
                    <p className="text-cream/60 mb-6">
                        A persona is one facet of your desires—your dom side, sub side, or an original character.
                    </p>

                    <div className="space-y-4">
                        <input
                            type="text"
                            value={editForm.display_name}
                            onChange={(e) => setEditForm(f => ({ ...f, display_name: e.target.value }))}
                            placeholder='e.g., "My Dom Side", "Luna (OC)"'
                            className="w-full px-4 py-3 bg-dark-800/80 border border-dark-600 text-cream placeholder-cream/40 focus:border-wine-500 focus:outline-none transition-colors"
                            maxLength={50}
                        />

                        {error && (
                            <p className="text-red-400 text-sm">{error}</p>
                        )}

                        <button
                            onClick={handleCreate}
                            disabled={saving || !editForm.display_name.trim()}
                            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <Heart className="w-4 h-4" />
                                    Create Persona
                                </>
                            )}
                        </button>

                        <Link to="/browse" className="btn-ghost w-full block">
                            Skip for now
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // No persona loaded and not authenticated
    if (!persona) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-charcoal">
                <div className="glass-card p-12 text-center max-w-md">
                    <User className="w-12 h-12 text-wine-400 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-cream mb-2">Profile Not Found</h2>
                    <p className="text-cream/60 mb-6">Sign in to create your profile.</p>
                    <Link to="/login" className="btn-primary">
                        Sign In
                    </Link>
                </div>
            </div>
        );
    }

    const tierConfig = TIER_CONFIG[persona.completion_tier] || TIER_CONFIG.incomplete;

    return (
        <>
            <div className="min-h-[calc(100vh-64px)] bg-charcoal py-8">
                <div className="max-w-4xl mx-auto px-6">
                    {/* Profile Header */}
                    <div className="glass-card p-8 mb-6">
                        <div className="flex items-start gap-6">
                            {/* Avatar with Completion Ring */}
                            <div className="flex flex-col items-center gap-4">
                                <div className="w-24 h-24 rounded-full bg-wine-900/40 flex items-center justify-center flex-shrink-0 border-2 border-wine-700/50">
                                    {persona.avatar_url ? (
                                        <img
                                            src={persona.avatar_url}
                                            alt={persona.display_name}
                                            className="w-full h-full rounded-full object-cover"
                                        />
                                    ) : (
                                        <User className="w-10 h-10 text-wine-400" />
                                    )}
                                </div>
                                {/* Mini completion ring */}
                                <ProfileCompletionRing
                                    tier={persona.completion_tier || 'incomplete'}
                                    ratingCount={persona.rating_count || 0}
                                    hasBio={!!persona.bio}
                                    hasRole={!!persona.role_preference}
                                    hasSafeWord={!!persona.persona_safety?.safe_word}
                                    size="sm"
                                    showDetails={false}
                                />
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                    {editing ? (
                                        <input
                                            type="text"
                                            value={editForm.display_name}
                                            onChange={(e) => setEditForm(f => ({ ...f, display_name: e.target.value }))}
                                            className="text-2xl font-display font-bold bg-charcoal-950 text-cream px-3 py-1 rounded border border-wine-700/50 focus:border-wine-500 outline-none"
                                        />
                                    ) : (
                                        <h1 className="text-2xl font-display font-bold text-cream">
                                            {persona.display_name}
                                        </h1>
                                    )}

                                    {/* Tier Badge */}
                                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${tierConfig.bg} ${tierConfig.color}`}>
                                        {tierConfig.label}
                                    </span>

                                    {/* Primary Badge */}
                                    {persona.is_primary && (
                                        <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold uppercase bg-amber-900/30 text-amber-400" title="Primary Persona">
                                            <Crown className="w-3 h-3" />
                                            Primary
                                        </span>
                                    )}

                                    {/* Visibility */}
                                    {persona.visibility === 'private' && (
                                        <Lock className="w-4 h-4 text-gray-500" title="Private" />
                                    )}
                                </div>

                                {/* Bio */}
                                {editing ? (
                                    <textarea
                                        value={editForm.bio}
                                        onChange={(e) => setEditForm(f => ({ ...f, bio: e.target.value }))}
                                        placeholder="Write something about yourself..."
                                        className="w-full h-24 bg-charcoal-950 text-cream/80 px-3 py-2 rounded border border-wine-700/50 focus:border-wine-500 outline-none resize-none text-sm"
                                    />
                                ) : (
                                    <p className="text-cream/60 text-sm mb-4">
                                        {persona.bio || 'No bio yet.'}
                                    </p>
                                )}

                                {/* Visibility selector when editing */}
                                {editing && (
                                    <div className="flex items-center gap-2 mt-3">
                                        <label className="text-sm text-cream/60">Visibility:</label>
                                        <select
                                            value={editForm.visibility}
                                            onChange={(e) => setEditForm(f => ({ ...f, visibility: e.target.value }))}
                                            className="bg-charcoal-950 text-cream text-sm px-2 py-1 rounded border border-wine-700/50"
                                        >
                                            <option value="private">Private</option>
                                            <option value="friends">Friends Only</option>
                                            <option value="public">Public</option>
                                        </select>
                                    </div>
                                )}

                                {/* Stats Row */}
                                {!editing && (
                                    <div className="flex items-center gap-6 text-sm">
                                        {persona.user_xp && (
                                            <div className="flex items-center gap-1.5 text-amber-400">
                                                <Trophy className="w-4 h-4" />
                                                <span>Level {persona.user_xp.level}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1.5 text-cream/50">
                                            <Star className="w-4 h-4" />
                                            <span>{persona.user_xp?.total_xp || 0} XP</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Edit Button */}
                            {isOwner && (
                                <div className="flex gap-2">
                                    {editing ? (
                                        <>
                                            <button
                                                onClick={handleSave}
                                                disabled={saving}
                                                className="btn-primary flex items-center gap-2"
                                            >
                                                {saving ? <Spinner size="xs" /> : <Save className="w-4 h-4" />}
                                                Save
                                            </button>
                                            <button
                                                onClick={() => setEditing(false)}
                                                className="btn-ghost"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            {!persona.is_primary && (
                                                <button
                                                    onClick={handleSetPrimary}
                                                    disabled={settingPrimary}
                                                    className="btn-ghost flex items-center gap-2 text-amber-400 hover:text-amber-300"
                                                    title="Set as Primary Persona"
                                                >
                                                    {settingPrimary ? <Spinner size="xs" /> : <Crown className="w-4 h-4" />}
                                                    Primary
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setEditing(true)}
                                                className="btn-ghost flex items-center gap-2"
                                            >
                                                <Edit2 className="w-4 h-4" />
                                                Edit
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    {isOwner && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <Link
                                to="/rate"
                                className="glass-card p-5 hover:border-wine-500/50 transition-colors group"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-lg bg-wine-900/30 flex items-center justify-center">
                                        <Heart className="w-6 h-6 text-wine-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-cream group-hover:text-wine-400 transition-colors">
                                            Rate Kinks
                                        </h3>
                                        <p className="text-sm text-cream/50">Build your preference profile</p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-cream/30 group-hover:text-wine-400" />
                                </div>
                            </Link>

                            <Link
                                to="/connections"
                                className="glass-card p-5 hover:border-wine-500/50 transition-colors group"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-lg bg-purple-900/30 flex items-center justify-center">
                                        <Users className="w-6 h-6 text-purple-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-cream group-hover:text-purple-400 transition-colors">
                                            Connections
                                        </h3>
                                        <p className="text-sm text-cream/50">Manage partners</p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-cream/30 group-hover:text-purple-400" />
                                </div>
                            </Link>

                            <button
                                onClick={() => setShowSettingsModal(true)}
                                className="glass-card p-5 hover:border-wine-500/50 transition-colors group text-left w-full"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center">
                                        <Settings className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-cream group-hover:text-gray-300 transition-colors">
                                            Settings
                                        </h3>
                                        <p className="text-sm text-cream/50">Details, safety & limits</p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-cream/30 group-hover:text-gray-300" />
                                </div>
                            </button>

                            {/* Manage Personas */}
                            <button
                                onClick={() => setShowPersonaModal(true)}
                                className="glass-card p-5 hover:border-amber-500/50 transition-colors group text-left w-full"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-lg bg-amber-900/30 flex items-center justify-center">
                                        <Crown className="w-6 h-6 text-amber-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-bold text-cream group-hover:text-amber-400 transition-colors">
                                            Personas
                                        </h3>
                                        <p className="text-sm text-cream/50">Switch or create new</p>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-cream/30 group-hover:text-amber-400" />
                                </div>
                            </button>
                        </div>
                    )}

                    {/* Chastity Widget (if applicable) */}
                    {persona.chastity_state && persona.chastity_state.status !== 'free' && (
                        <div className="glass-card p-6 mb-6 border-l-4 border-wine-500">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 rounded-full bg-wine-900/50 flex items-center justify-center">
                                    <Lock className="w-7 h-7 text-wine-400" />
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm text-wine-400 font-bold uppercase tracking-wider">
                                        Chastity Status
                                    </div>
                                    <div className="text-2xl font-bold text-cream">
                                        {persona.chastity_state.status === 'keyholder'
                                            ? 'Held by Keyholder'
                                            : persona.chastity_state.status === 'self_locked'
                                                ? 'Self-Locked'
                                                : 'Timed Lock'}
                                    </div>
                                    {persona.chastity_state.locked_since && (
                                        <div className="text-sm text-cream/50">
                                            Locked since {new Date(persona.chastity_state.locked_since).toLocaleDateString()}
                                        </div>
                                    )}
                                </div>
                                <div className="text-right">
                                    <div className="text-3xl font-bold text-wine-400">
                                        {Math.floor((Date.now() - new Date(persona.chastity_state.locked_since)) / (1000 * 60 * 60 * 24))}
                                    </div>
                                    <div className="text-xs text-cream/50 uppercase">Days</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Profile Content - Only for owner */}
                    {isOwner && (
                        <div className="space-y-4">
                            {/* Lust Witch */}
                            <LustWitchWidget />

                            {/* For You Recommendations */}
                            <ForYouWidget />

                            {/* My Rated Kinks - at bottom */}
                            <MyRatings personaId={persona.id} />
                        </div>
                    )}

                    {/* View-only profile details for non-owners */}
                    {!isOwner && persona.persona_details && (
                        <div className="glass-card p-6">
                            <h3 className="font-bold text-cream mb-4">About</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                {persona.persona_details.role_preference && (
                                    <div>
                                        <div className="text-cream/50 text-xs uppercase mb-1">Role</div>
                                        <div className="text-cream capitalize">{persona.persona_details.role_preference}</div>
                                    </div>
                                )}
                                {persona.persona_details.experience_level && (
                                    <div>
                                        <div className="text-cream/50 text-xs uppercase mb-1">Experience</div>
                                        <div className="text-cream capitalize">{persona.persona_details.experience_level}</div>
                                    </div>
                                )}
                                {persona.persona_details.orientation && (
                                    <div>
                                        <div className="text-cream/50 text-xs uppercase mb-1">Orientation</div>
                                        <div className="text-cream">{persona.persona_details.orientation}</div>
                                    </div>
                                )}
                                {persona.persona_details.relationship_style && (
                                    <div>
                                        <div className="text-cream/50 text-xs uppercase mb-1">Relationship Style</div>
                                        <div className="text-cream">{persona.persona_details.relationship_style}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Persona Management Modal */}
            <AnimatePresence>
                {showPersonaModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowPersonaModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="glass-card p-6 w-full max-w-md"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-display font-bold text-cream">Your Personas</h2>
                                <button
                                    onClick={() => setShowPersonaModal(false)}
                                    className="p-2 text-cream/50 hover:text-cream"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <p className="text-sm text-cream/60 mb-4">
                                Personas let you explore different facets of yourself—your dom side, sub side, or original characters.
                            </p>

                            {/* Persona List */}
                            <div className="space-y-2 mb-6">
                                {personas.map(p => (
                                    <div
                                        key={p.id}
                                        className={`p-4 border transition-colors cursor-pointer ${p.id === activePersona?.id
                                            ? 'border-wine-500 bg-wine-900/20'
                                            : 'border-dark-600 hover:border-wine-500/50'
                                            }`}
                                        onClick={() => {
                                            setActivePersona(p);
                                            setShowPersonaModal(false);
                                            // Navigate to this persona's profile
                                            window.location.href = `/profile/${p.id}`;
                                        }}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-wine-900/50 flex items-center justify-center">
                                                    <User className="w-5 h-5 text-wine-400" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-cream">{p.display_name}</span>
                                                        {p.is_primary && (
                                                            <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-amber-900/30 text-amber-400 uppercase">
                                                                <Crown className="w-2.5 h-2.5" />
                                                                Primary
                                                            </span>
                                                        )}
                                                    </div>
                                                    {p.bio && (
                                                        <p className="text-xs text-cream/50 line-clamp-1">{p.bio}</p>
                                                    )}
                                                </div>
                                            </div>
                                            {p.id === activePersona?.id && (
                                                <Star className="w-4 h-4 text-wine-400" />
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Create New Persona */}
                            <button
                                onClick={() => {
                                    setShowPersonaModal(false);
                                    setCreatingNewPersona(true);
                                }}
                                className="btn-primary w-full flex items-center justify-center gap-2"
                            >
                                <Heart className="w-4 h-4" />
                                Create New Persona
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Settings Modal */}
            <AnimatePresence>
                {showSettingsModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
                        onClick={() => setShowSettingsModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={e => e.stopPropagation()}
                            className="glass-card p-6 w-full max-w-2xl my-8"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-display font-bold text-cream flex items-center gap-2">
                                    <Settings className="w-5 h-5 text-gray-400" />
                                    Profile Settings
                                </h2>
                                <button
                                    onClick={() => setShowSettingsModal(false)}
                                    className="p-2 text-cream/50 hover:text-cream"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Personal Details Form */}
                                <PersonalDetailsForm
                                    personaId={persona.id}
                                    initialData={persona.persona_details || {}}
                                    collapsible={true}
                                    defaultExpanded={true}
                                />

                                {/* Safety Panel */}
                                <SafetyPanel
                                    personaId={persona.id}
                                    initialData={persona.persona_safety || {}}
                                    collapsible={true}
                                    defaultExpanded={false}
                                />

                                {/* Hard Limits Panel */}
                                <HardLimitsPanel
                                    personaId={persona.id}
                                    collapsible={true}
                                    defaultExpanded={false}
                                />
                            </div>

                            <div className="mt-6 pt-4 border-t border-dark-700">
                                <button
                                    onClick={() => setShowSettingsModal(false)}
                                    className="btn-ghost w-full"
                                >
                                    Done
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Persona Creation Modal */}
            <PersonaCreationModal
                isOpen={creatingNewPersona}
                onClose={() => setCreatingNewPersona(false)}
                onSuccess={() => {
                    setCreatingNewPersona(false);
                    fetchPersonas();
                }}
            />
        </>
    );
}

export default ProfilePage;
